import { ChangeEvent, useMemo, useState } from 'react'

import cn from 'classnames'
import { Loader2 } from 'lucide-react'

import { getTokenMetadata } from '@src/api/solana.api'
import WarningIcon from '@src/components/icons/WarningIcon'
import {
    ChainId,
    DEFAULT_NETWORK_ICONS,
    NATIVE_TOKEN_ADDRESS,
    getChainName,
    getChainNameFromChainId,
} from '@src/configs/chainInfo'
import { copy } from '@src/constants/application'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { ConfigLoadingStatus } from '@src/enums'
import { useBitcoinPrice } from '@src/hooks/useBitcoinPrice'
import { useFetchAllTokens } from '@src/hooks/useFetchAllTokens'
import SelectTokenModal from '@src/pages/Exchange/components/TokenSelectorModal'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { useExchangeDerivedState } from '@src/pages/Exchange/hooks/useExchangeDerivedState'
import { IExchangeData } from '@src/types/exchange'
import { IQuoteResponse } from '@src/types/order'
import { IToken } from '@src/types/token'
import { Balance, computeUSDAmount, createContextLogger, formatNumberStringWithCommas } from '@src/utils'
import { computeBitcoinUSDAmount, isBitcoinAsset } from '@src/utils/bitcoinUtils'
import { isFeatherChain } from '@src/utils/chainUtils'
import { useConfigStore } from '@src/zustand_stores'

/**
 * Props for the FromSection
 */
interface IFromSectionProps {
    sellData: IExchangeData
    buyData: IExchangeData
    /**
     * Whether the user's wallet is connected to the correct chain for this "sellData".
     * If false, we might show a disabled input or hide the 'Max' button, etc.
     */
    isCorrectWalletConnected: boolean
    isGasEstimationFetched?: boolean

    /** If the user's balance < sellData.value, show an "Insufficient" style. */
    isInsufficientBalance: boolean

    /**
     * The parent container's local string for the numeric input.
     * e.g. '0.002'
     */
    sellAmountString: string

    quoteResponse: IQuoteResponse

    /** Called whenever the user types in the input. */
    onChangeSellValue: (e: ChangeEvent<HTMLInputElement>) => void

    /** Called when the user clicks "Max". */
    onSetMax: () => void

    onTokenSelectionChange: (
        newData: { token?: IToken; chainId?: number; value?: bigint },
        type: 'buy' | 'sell'
    ) => void
}

const logger = createContextLogger('FromSection')

export function FromSection({
    sellData,
    buyData,
    isCorrectWalletConnected,
    isInsufficientBalance,
    sellAmountString,
    isGasEstimationFetched,
    onChangeSellValue,
    onSetMax,
    onTokenSelectionChange,
}: IFromSectionProps) {
    const { isSellBalanceLoading, sellBalance, sellBalanceStabilized } = useExchangeBalances()
    const { disabledFromChains: disabledChains } = useExchangeDerivedState()
    const { hasInsufficientGas, gasAmountNeededString } = useExchangeState()
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const configLoading = useConfigStore(config => config.loading)
    const { isLoading: zustandTokensLoading } = useFetchAllTokens()
    const [isSelectTokenModalOpen, setIsSelectTokenModalOpen] = useState(false)

    const isConfigurationLoading = useMemo(
        () =>
            configLoading === ConfigLoadingStatus.loading_not_yet_started ||
            configLoading === ConfigLoadingStatus.loading ||
            zustandTokensLoading,
        [configLoading, zustandTokensLoading]
    )

    // Get Bitcoin price from our custom hook for Bitcoin assets
    const { price: bitcoinPrice, loading: bitcoinPriceLoading } = useBitcoinPrice()

    const sellBalanceInUSD = useMemo<string | undefined>(() => {
        const parsedValue = new Balance(sellData.token).parseString(sellAmountString).bigintValue

        // Handle Bitcoin assets differently with our custom implementation
        if (isBitcoinAsset(sellData.token)) {
            const btcUsdAmount = computeBitcoinUSDAmount(parsedValue, bitcoinPrice, sellData.token)
            return btcUsdAmount ? `$${formatNumberStringWithCommas(btcUsdAmount)}` : undefined
        }

        // Regular token handling
        const a = computeUSDAmount(sellData.token, parsedValue)
        if (a === undefined) {
            return undefined
        } else {
            return `$${formatNumberStringWithCommas(a.toString())}`
        }
    }, [sellData.token, sellAmountString, bitcoinPrice])

    const maxButtonIsLoading = useMemo(() => {
        return sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId] && !isGasEstimationFetched
    }, [sellData.token, sellData.chainId, isGasEstimationFetched])

    return (
        <div className='space-y-2 rounded-[10px] border-mach-surface-border bg-mach-surface-background p-5'>
            {/* Header row with Sell label and balance */}
            <div className='flex items-center justify-between'>
                <span className='text-fluid-lg font-bold !leading-5 text-white'>Pay</span>
                {isCorrectWalletConnected && (
                    <div className='flex items-center gap-2'>
                        <span
                            className={cn(
                                'text-fluid-sm !leading-5 text-mach-sub-text',
                                isInsufficientBalance && 'text-error'
                            )}
                        >
                            {isSellBalanceLoading ? (
                                <span className='inline-flex items-center gap-1'>
                                    <Loader2 className='h-3 w-3 animate-spin' />
                                    {copy.balanceLabel}:
                                </span>
                            ) : (
                                `${copy.balanceLabel}:`
                            )}{' '}
                            {!isSellBalanceLoading && sellBalanceStabilized
                                ? `${
                                      sellBalance !== undefined
                                          ? sellBalance.formatWithMagnitudes(MAX_TOKEN_DISPLAY_DECIMALS)
                                          : 0
                                  } ${sellData.token.symbol}`
                                : '—'}
                        </span>
                        <button
                            className='h-[20px] w-[33px] rounded-md border-mach-button-tabs-border bg-white px-[4px] py-[2px] text-fluid-xs text-[#111112] hover:cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50'
                            onClick={onSetMax}
                            disabled={maxButtonIsLoading || isConfigurationLoading}
                        >
                            {maxButtonIsLoading ? <Loader2 className='mx-auto h-3 w-3 animate-spin' /> : 'Max'}
                        </button>
                    </div>
                )}
            </div>

            {/* Input and token selection */}
            <div className='flex items-start'>
                {/* Input field */}
                <div className='flex flex-1 flex-col gap-y-2'>
                    <input
                        className={cn('w-full bg-transparent text-left text-fluid-4xl font-semibold !leading-[44px]', {
                            'text-error': isCorrectWalletConnected && isInsufficientBalance,
                            'cursor-not-allowed opacity-50': isConfigurationLoading,
                        })}
                        placeholder={isConfigurationLoading ? 'Loading...' : '0'}
                        value={sellAmountString}
                        onChange={e => {
                            logger.info('should call onChangeSellValue')
                            onChangeSellValue(e)
                        }}
                        inputMode='decimal'
                        required
                        formNoValidate
                        disabled={isConfigurationLoading}
                    />
                    <div className='text-fluid-base text-mach-sub-text'>
                        {isBitcoinAsset(sellData.token) && bitcoinPriceLoading ? (
                            <span className='inline-flex items-center gap-1'>
                                <Loader2 className='h-3 w-3 animate-spin' />
                                Loading price...
                            </span>
                        ) : (
                            (sellBalanceInUSD ?? '$0.00')
                        )}
                    </div>
                </div>

                {/* Token selection */}
                <div className='flex-shrink-0 hover:cursor-pointer hover:opacity-80'>
                    <div
                        className='flex cursor-pointer flex-col items-end gap-y-2'
                        onClick={() => setIsSelectTokenModalOpen(true)}
                    >
                        {/* Token symbol and icon row */}
                        <div className='flex items-center gap-2 py-1'>
                            {isConfigurationLoading ? (
                                <div className='h-7 w-7 animate-pulse rounded-full bg-mach-surface-border'></div>
                            ) : (
                                <img
                                    src={sellData.token.logoURI}
                                    alt={sellData.token.symbol}
                                    className='h-7 w-7 rounded-full'
                                    onError={e => {
                                        // Prevent infinite loop if fallback also fails
                                        if (e.currentTarget.src === '/assets/token/unknown.png') {
                                            return
                                        }

                                        // If we have a Solana token, try to fetch metadata directly
                                        if (sellData.chainId === ChainId.Solana) {
                                            getTokenMetadata(sellData.token.address)
                                                .then(metadata => {
                                                    if (metadata?.logoURI) {
                                                        e.currentTarget.src = metadata.logoURI
                                                    } else {
                                                        e.currentTarget.src = '/assets/token/unknown.png'
                                                    }
                                                })
                                                .catch(() => {
                                                    e.currentTarget.src = '/assets/token/unknown.png'
                                                })
                                        } else {
                                            e.currentTarget.src = '/assets/token/unknown.png'
                                        }
                                    }}
                                />
                            )}
                            <div className='flex items-center'>
                                {isConfigurationLoading ? (
                                    <div className='h-8 w-16 animate-pulse rounded-md bg-mach-surface-border'></div>
                                ) : (
                                    <span className='text-3xl font-semibold'>{sellData.token.symbol}</span>
                                )}

                                {/* Dropdown with dark background */}
                                <div className='ml-3 flex items-center justify-center rounded-lg bg-mach-gray p-[5px]'>
                                    <svg
                                        width='20'
                                        height='20'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            d='M6 9L12 15L18 9'
                                            stroke='#ffffff'
                                            strokeWidth='2'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Chain name row */}
                        <div className='flex items-center gap-1 text-base text-gray-400'>
                            <span className='mr-1 !leading-6'>on</span>
                            {/* Chain icon */}
                            <img
                                src={
                                    availableNetworks[sellData.chainId]?.icon || DEFAULT_NETWORK_ICONS[sellData.chainId]
                                }
                                alt={`Chain ${sellData.chainId}`}
                                className='h-[14px] w-[14px] rounded-full text-white'
                                onError={e => {
                                    e.currentTarget.src = '/assets/token/unknown.png'
                                }}
                            />
                            <span className='!leading-6 text-white'>
                                {getChainName(getChainNameFromChainId(sellData.chainId))}
                            </span>
                        </div>
                    </div>

                    {/* Token selection modal */}
                    <SelectTokenModal
                        isOpen={isSelectTokenModalOpen}
                        onClose={() => setIsSelectTokenModalOpen(false)}
                        selectedChain={sellData.chainId}
                        setSelectedChain={chainId => onTokenSelectionChange({ chainId: Number(chainId) }, 'sell')}
                        selectedToken={sellData.token}
                        setSelectedToken={token => {
                            onTokenSelectionChange({ token, chainId: token.chainId }, 'sell')
                            setIsSelectTokenModalOpen(false)
                        }}
                        availableChains={availableNetworks}
                        disabledChains={disabledChains || []}
                        isSolanaSwap={buyData.chainId === ChainId.Solana || sellData.chainId === ChainId.Solana}
                        buyData={buyData}
                        sellData={sellData}
                        side='sell'
                    />
                </div>
            </div>

            {/* Show insufficient gas warning when wallet is connected and has insufficient gas for swap */}
            {hasInsufficientGas &&
                !isFeatherChain(buyData.chainId) &&
                isCorrectWalletConnected &&
                !isSellBalanceLoading &&
                sellBalanceStabilized &&
                sellData.value !== BigInt(0) &&
                buyData.value !== BigInt(0) && (
                    <div className='flex items-center justify-start gap-4 text-xs text-mach-sub-text sm:text-sm'>
                        <WarningIcon></WarningIcon> At least {gasAmountNeededString} is required to execute this swap
                    </div>
                )}
        </div>
    )
}
